import { EyeIcon } from "@heroicons/react/24/outline";
import {
  useCollector,
  usePaginatedCollector,
} from "app/integration/common/hooks";
import {
  IconButton,
} from "app/components/atoms/button";
import { SearchInput } from "app/components/atoms/input";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import MainLayout from "app/layouts/main";
import { useEffect, useState } from "react";
import { Badge } from "app/components/atoms/badge";
import { useParams } from "react-router-dom";
import { FormSummary } from "app/components/atoms/layout";
import api from "app/integration/api";

const breadcrumbItems = [
  { label: "Stock Opname", href: "/stock-opname" },
  { label: "Detail Stock Opname", href: "#" },
];

function StockOpname() {
  const { id } = useParams();
  const [params, setParams] = useState({
    id: id,
  });

  useEffect(() => {
    collect(params);
  }, [params]);

  const [
    stockOpnames,
    setStockOpnames,
    stockOpnamesLoader,
    collect,
  ] = usePaginatedCollector({ modelName: "stockOpname", params: params });

  const [productVariants] = useCollector({ modelName: "productVariant" });
  const [productCodes, setProductCodes] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const stockOpname = stockOpnames?.find((item) => item.id === parseInt(id));

  // Fetch product codes when stockOpname changes
  useEffect(() => {
    const fetchProductCodes = async () => {
      if (!stockOpname?.code_list?.length) {
        setIsLoading(false);
        return;
      }

      const codes = {};
      try {
        for (const qrCode of stockOpname.code_list) {
          try {
            const response = await api.productCode.index({ qr_code: qrCode });
            if (response?.data?.[0]) {
              codes[qrCode] = response.data[0];
            } else {
              codes[qrCode] = { code: 'Code not found' };
            }
          } catch (error) {
            console.error(`Error fetching product code for QR code ${qrCode}:`, error);
            codes[qrCode] = { code: 'Error loading code' };
          }
        }
        setProductCodes(codes);
      } catch (error) {
        console.error('Error in fetchProductCodes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductCodes();
  }, [stockOpname]);

  return (
    <MainLayout
      activeSidebarNavigation="warehouse"
      activeSidebarChild="stock_opname"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Stock Opname Detail"
    >
      <FormSummary className="w-full">
        <Table.Wrapper asIndex={false}>
          <Table.Body>
            <Table.BodyRow>
              <Table.Cell>Nama Produk</Table.Cell>
              <Table.Cell>: {productVariants?.find((item) => item.id === stockOpname?.product_variant_id)?.product_detail?.name || '-'}</Table.Cell>
              <Table.Cell colSpan={2}></Table.Cell>
              <Table.Cell>Product Variant</Table.Cell>
              <Table.Cell>: {productVariants?.find((item) => item.id === stockOpname?.product_variant_id)?.color?.name}, {productVariants?.find((item) => item.id === stockOpname?.product_variant_id)?.size?.name || '-'}</Table.Cell>
            </Table.BodyRow>
            <Table.BodyRow>
              <Table.Cell>Stock Fisik</Table.Cell>
              <Table.Cell>: {stockOpname?.physical_stock || '-'}</Table.Cell>
              <Table.Cell colSpan={2}></Table.Cell>
              <Table.Cell>Stock Sistem</Table.Cell>
              <Table.Cell>: {stockOpname?.system_stock || '-'}</Table.Cell>
            </Table.BodyRow>
            <Table.BodyRow>
              <Table.Cell>Selisih</Table.Cell>
              <Table.Cell>: {stockOpname?.difference || '-'}</Table.Cell>
              <Table.Cell colSpan={2}></Table.Cell>
              <Table.Cell>Tanggal</Table.Cell>
              <Table.Cell>: {stockOpname?.opname_date ? new Date(stockOpname.opname_date).toLocaleDateString() : '-'}</Table.Cell>
            </Table.BodyRow>
            <Table.BodyRow>
              <Table.Cell>Deskripsi</Table.Cell>
              <Table.Cell colSpan={5}>: {stockOpname?.description || '-'}</Table.Cell>
            </Table.BodyRow>
          </Table.Body>
        </Table.Wrapper>
      </FormSummary>
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <Table.SearchBarItemWrapper>
              <SearchInput testId="stock-opname-detail-search-input"
                placeholder="Cari Stock Opname"
                type="text"
                value={params.stock_opname_search}
                // onChange={handleQueryChange}
                onChange={(e) =>
                  setParams({ ...params, stock_opname_search: e.target.value })
                }
              />
            </Table.SearchBarItemWrapper>
          </Table.SearchBarWrapper>
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>REF</Table.Heading>
              <Table.Heading>Kode Produk</Table.Heading>
              <Table.Heading>Produk</Table.Heading>
              <Table.Heading>Varian</Table.Heading>
              <Table.Heading>Tanggal</Table.Heading>
              <Table.Heading>Status</Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {stockOpname?.code_list?.length > 0 ? (
              stockOpname.code_list.map((qrCode, index) => (
                <Table.BodyRow key={index}>
                  <Table.Cell>{isLoading 
                        ? 'Loading...' 
                        : productCodes[qrCode]?.ref || 'No code available'}</Table.Cell>
                  <Table.Cell>
                    <div className="p-1 bg-gray-100 rounded">
                      {isLoading 
                        ? 'Loading...' 
                        : productCodes[qrCode]?.qr_code || 'No code available'}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    {productVariants?.find((item) => item.id === stockOpname?.product_variant_id)?.product_detail?.name || '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {productVariants?.find((item) => item.id === stockOpname?.product_variant_id)?.color?.name || '-'}, 
                    {productVariants?.find((item) => item.id === stockOpname?.product_variant_id)?.size?.name || '-'}
                  </Table.Cell>
                  <Table.Cell>
                    <Typography.Date date={stockOpname?.opname_date} format="dd MMMM yyyy hh:mm:ss" />
                  </Table.Cell>
                  <Table.Cell>
                    <Badge 
                      color={
                        stockOpname?.status === "pending" ? "yellow" : 
                        stockOpname?.status === "approved" ? "green" : "red"
                      }
                    >
                      {stockOpname?.status || 'unknown'}
                    </Badge>
                  </Table.Cell>
                </Table.BodyRow>
              ))
            ) : (
              <Table.BodyRow>
                <Table.Cell colSpan={9} className="text-center text-gray-500 py-4">
                  No product codes available
                </Table.Cell>
              </Table.BodyRow>
            )}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
    </MainLayout>
  );
}

export default StockOpname;
